import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  useTheme 
} from '@mui/material';
import { motion } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const faqs = [
  {
    question: 'Как интегрировать GameGuard AI в мою Unity-игру?',
    answer: 'GameGuard AI разработан для максимально простой интеграции. Просто скачайте наш пакет из Unity Asset Store, добавьте его на сцену через удобное меню и все готово! Никакого программирования или сложных настроек не требуется.'
  },
  {
    question: 'Как GameGuard AI влияет на производительность игры?',
    answer: 'Наша система разработана с учетом минимального влияния на производительность. Многочисленные тесты показывают, что в большинстве игр влияние составляет менее 0,1% от общей нагрузки, что практически незаметно для игроков.'
  },
  {
    question: 'Какие типы читов распознает GameGuard AI?',
    answer: 'GameGuard AI автоматически обнаруживает спид-хаки, манипуляции с игровым временем и множество других распространенных методов нечестной игры. Наша система, построенная на искусственном интеллекте, изучает особенности вашей игры и способна выявлять даже нестандартные методы читерства с высокой точностью.'
  },
  {
    question: 'С какими версиями Unity совместим GameGuard AI?',
    answer: 'GameGuard AI полностью совместим со всеми версиями Unity 2019.4 и выше. Если вы используете более раннюю версию движка, свяжитесь с нашей технической поддержкой для получения информации о возможностях интеграции.'
  },
  {
    question: 'Что происходит после обнаружения чита?',
    answer: 'Вы полностью контролируете, как ваша игра реагирует на обнаруженные читы. Диапазон действий варьируется от предупреждений до мгновенной блокировки. Система предоставляет подробную аналитическую информацию по каждому инциденту, позволяя вам принимать взвешенные решения.'
  },
  {
    question: 'Можно ли настроить чувствительность обнаружения?',
    answer: 'Да! Хотя GameGuard AI работает эффективно с настройками по умолчанию для большинства игр, вы можете тонко настроить параметры обнаружения через интуитивно понятную панель управления. Для продвинутых пользователей также доступны инструменты на Python, позволяющие адаптировать систему под специфику вашей игровой механики.'
  }
];

const FaqSection: React.FC = () => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<number | false>(false);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      id="faq"
      sx={{
        py: 12,
        background: theme.palette.background.default,
      }}
    >
      <Container>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              component="h2"
              color="primary"
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Часто задаваемые вопросы
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary', maxWidth: '800px', mx: 'auto' }}>
              Вся необходимая информация о GameGuard AI
            </Typography>
          </motion.div>
        </Box>

        <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Accordion
                expanded={expanded === index}
                onChange={handleChange(index)}
                elevation={0}
                sx={{
                  mb: 2,
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: '8px !important',
                  '&:before': {
                    display: 'none',
                  },
                  backgroundColor: expanded === index ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                  transition: 'background-color 0.3s ease',
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon 
                      sx={{ 
                        color: expanded === index ? theme.palette.primary.main : 'text.secondary',
                        transition: 'transform 0.3s ease, color 0.3s ease',
                        transform: expanded === index ? 'rotate(180deg)' : 'rotate(0deg)',
                      }} 
                    />
                  }
                  sx={{
                    '&.Mui-expanded': {
                      minHeight: 0,
                    }
                  }}
                >
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </Box>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Box 
            sx={{ 
              mt: 8, 
              textAlign: 'center',
              p: 5, 
              backgroundColor: 'rgba(25, 118, 210, 0.05)', 
              borderRadius: 4,
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Остались вопросы?
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Наша команда готова помочь вам с внедрением GameGuard AI в ваш проект.
            </Typography>
            <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
              Напишите нам: support@gameguardai.com
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FaqSection; 